// URM API client for interacting with the Go backend
// Using empty string for relative URLs to go through Next.js proxy
const API_BASE_URL = process.env.NEXT_PUBLIC_URM_API_URL || '';

// ============================================================================
// CSRF Token Management
// ============================================================================

let csrfToken: string | null = null;
let csrfTokenExpiry: Date | null = null;
let csrfTokenFetchPromise: Promise<string> | null = null;

/**
 * Fetches a new CSRF token from the backend
 */
async function fetchCSRFToken(): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.statusText}`);
    }

    const data = await response.json();
    csrfToken = data.csrf_token;
    csrfTokenExpiry = new Date(data.expires_at);
    if (!csrfToken) {
      throw new Error('CSRF token is null');
    }

    console.log('[CSRF] Fetched new CSRF token, expires:', csrfTokenExpiry);
    return csrfToken;
  } catch (error) {
    console.error('[CSRF] Error fetching CSRF token:', error);
    // Clear the promise so next attempt will try again
    csrfTokenFetchPromise = null;
    throw error;
  }
}

/**
 * Gets a valid CSRF token, fetching a new one if needed
 * Implements request deduplication to prevent multiple simultaneous token fetches
 */
async function getCSRFToken(): Promise<string> {
  // Check if we have a valid cached token
  if (csrfToken && csrfTokenExpiry && new Date() < csrfTokenExpiry) {
    console.log('[CSRF] Using cached token');
    return csrfToken;
  }

  // Check if there's already a token fetch in progress
  if (csrfTokenFetchPromise) {
    console.log('[CSRF] Token fetch already in progress, waiting for it...');
    return csrfTokenFetchPromise;
  }

  // Token expired or doesn't exist, fetch a new one
  console.log('[CSRF] Token expired or missing, fetching new one');
  csrfTokenFetchPromise = fetchCSRFToken().finally(() => {
    // Clear the promise after completion (success or failure)
    csrfTokenFetchPromise = null;
  });

  return csrfTokenFetchPromise;
}

/**
 * Clears the cached CSRF token (called on logout)
 */
export function clearCSRFToken(): void {
  csrfToken = null;
  csrfTokenExpiry = null;
  console.log('[CSRF] Cleared cached CSRF token');
}

/**
 * Adds CSRF token to request headers
 * EXPORTED for use in components that make direct fetch calls
 */
export async function addCSRFHeader(headers: HeadersInit = {}): Promise<HeadersInit> {
  try {
    const token = await getCSRFToken();
    return {
      ...headers,
      'X-CSRF-Token': token,
    };
  } catch (error) {
    console.error('[CSRF] Failed to add CSRF token to headers:', error);
    // Return headers without CSRF token - request will fail but with clear error
    return headers;
  }
}

export interface APIInfo {
  group: string;
  version: string;
  type: string;
}

export interface DiscoverAPIsResponse {
  apis: APIInfo[];
}

export interface CountResourcesRequest {
  group: string;
  version: string;
  type: string;
  path?: string;
  recursive?: boolean;
}

export interface CountResourcesResponse {
  count: number;
  group: string;
  version: string;
  type: string;
}

export interface ResourceCount {
  group: string;
  version: string;
  type: string;
  count: number;
}

/**
 * Check if the URM backend is healthy
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      credentials: 'include',
    });
    return response.ok;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
}

/**
 * Discover available URM APIs
 * @param groups Optional array of groups to filter by
 */
export async function discoverAPIs(groups?: string[]): Promise<DiscoverAPIsResponse> {
  try {
    // Build URL - if API_BASE_URL is empty, use relative URL
    let url: string;
    if (API_BASE_URL) {
      const urlObj = new URL(`${API_BASE_URL}/api/discover`);
      if (groups && groups.length > 0) {
        urlObj.searchParams.set('groups', groups.join(','));
      }
      url = urlObj.toString();
    } else {
      // Use relative URL with query params
      url = '/api/discover';
      if (groups && groups.length > 0) {
        url += `?groups=${encodeURIComponent(groups.join(','))}`;
      }
    }

    const response = await fetch(url, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to discover APIs: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error discovering APIs:', error);
    throw error;
  }
}

/**
 * Count resources for a specific Group/Version/Type
 */
export async function countResources(request: CountResourcesRequest): Promise<CountResourcesResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/count`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        path: request.path || '/',
        recursive: request.recursive !== false, // default to true
      }),
    });

    if (!response.ok) {
      // Log the error but return a zero count instead of throwing
      console.error(`Failed to count resources for ${request.group}/${request.version}/${request.type}: ${response.statusText}`);
      return {
        group: request.group,
        version: request.version,
        type: request.type,
        count: 0,
      };
    }

    return await response.json();
  } catch (error) {
    console.error(`Error counting resources for ${request.group}/${request.version}/${request.type}:`, error);
    // Return zero count instead of throwing
    return {
      group: request.group,
      version: request.version,
      type: request.type,
      count: 0,
    };
  }
}

/**
 * Get resource counts for all discovered APIs
 * Note: This function processes APIs in batches to avoid overwhelming the browser/backend
 */
export async function getAllResourceCounts(groups?: string[]): Promise<ResourceCount[]> {
  try {
    // First discover all APIs
    const { apis } = await discoverAPIs(groups);

    // Limit to first 50 APIs to avoid timeout issues
    // For production, you'd want to implement proper pagination or batch processing
    const apisToCount = apis.slice(0, 50);

    console.log(`Counting resources for ${apisToCount.length} of ${apis.length} total APIs`);

    // Process in batches of 10 to avoid overwhelming the system
    const batchSize = 10;
    const results: ResourceCount[] = [];

    for (let i = 0; i < apisToCount.length; i += batchSize) {
      const batch = apisToCount.slice(i, i + batchSize);

      const batchPromises = batch.map(async (api) => {
        try {
          const result = await countResources({
            group: api.group,
            version: api.version,
            type: api.type,
            path: '/',
            recursive: true,
          });
          return result;
        } catch (error) {
          console.error(`Error counting resources for ${api.group}/${api.version}/${api.type}:`, error);
          // Return zero count on error
          return {
            group: api.group,
            version: api.version,
            type: api.type,
            count: 0,
          };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    // Filter out resources with zero count
    return results.filter(r => r.count > 0);
  } catch (error) {
    console.error('Error getting all resource counts:', error);
    throw error;
  }
}

/**
 * Get total resource count using VirtualResource API
 * VirtualResource represents all resources in the system
 */
export async function getTotalResourceCount(): Promise<number> {
  try {
    const result = await countResources({
      group: 'system.resource.api.sap',
      version: 'v1',
      type: 'VirtualResource',
      path: '/',
      recursive: true,
    });
    return result.count;
  } catch (error) {
    console.error('Error getting total resource count:', error);
    throw error;
  }
}

/**
 * Get resource count by status using VirtualResource API
 */
export async function getResourceCountByStatus(statusFilter: string): Promise<number> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/count`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: 'system.resource.api.sap',
        version: 'v1',
        type: 'VirtualResource',
        path: '/',
        recursive: true,
        statusFilter: statusFilter,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to count resources by status: ${response.statusText}`);
    }

    const data = await response.json();
    return data.count;
  } catch (error) {
    console.error(`Error getting ${statusFilter} resource count:`, error);
    throw error;
  }
}

/**
 * Specific resource type for key metrics
 */
export interface KeyResourceType {
  group: string;
  version: string;
  type: string;
  displayName: string;
  count?: number;
  readyCount?: number;
  notReadyCount?: number;
}

/**
 * Get counts for key resource types
 */
export async function getKeyResourceTypeCounts(): Promise<KeyResourceType[]> {
  const keyTypes: KeyResourceType[] = [
    {
      group: 'system.resource.api.sap',
      version: 'v2',
      type: 'TechnicalClient',
      displayName: 'Technical Clients v2',
    },
    {
      group: 'system.resource.api.sap',
      version: 'v1',
      type: 'Node',
      displayName: 'Nodes',
    },
    {
      group: 'policies.resource.api.sap',
      version: 'v1',
      type: 'Role',
      displayName: 'Roles',
    },
  ];

  try {
    // Fetch counts in parallel
    const countPromises = keyTypes.map(async (resourceType) => {
      try {
        // Fetch total, ready, and not-ready counts in parallel
        const [totalResult, _readyCount, _notReadyCount] = await Promise.all([
          countResources({
            group: resourceType.group,
            version: resourceType.version,
            type: resourceType.type,
            path: '/',
            recursive: true,
          }),
          getResourceCountByStatus('ready').catch(() => 0),
          getResourceCountByStatus('not-ready').catch(() => 0),
        ]);

        // Get status-specific counts for this resource type
        const readyForTypeHeaders = await addCSRFHeader({ 'Content-Type': 'application/json' });
        const readyForType = await fetch(`${API_BASE_URL}/api/count`, {
          method: 'POST',
          headers: readyForTypeHeaders,
          credentials: 'include',
          body: JSON.stringify({
            group: resourceType.group,
            version: resourceType.version,
            type: resourceType.type,
            path: '/',
            recursive: true,
            statusFilter: 'ready',
          }),
        }).then(res => res.json()).then(data => data.count).catch(() => 0);

        const notReadyForTypeHeaders = await addCSRFHeader({ 'Content-Type': 'application/json' });
        const notReadyForType = await fetch(`${API_BASE_URL}/api/count`, {
          method: 'POST',
          headers: notReadyForTypeHeaders,
          credentials: 'include',
          body: JSON.stringify({
            group: resourceType.group,
            version: resourceType.version,
            type: resourceType.type,
            path: '/',
            recursive: true,
            statusFilter: 'not-ready',
          }),
        }).then(res => res.json()).then(data => data.count).catch(() => 0);

        return {
          ...resourceType,
          count: totalResult.count,
          readyCount: readyForType,
          notReadyCount: notReadyForType,
        };
      } catch (error) {
        console.error(`Error counting ${resourceType.type}:`, error);
        return {
          ...resourceType,
          count: 0,
          readyCount: 0,
          notReadyCount: 0,
        };
      }
    });

    return await Promise.all(countPromises);
  } catch (error) {
    console.error('Error getting key resource type counts:', error);
    throw error;
  }
}

/**
 * Daily resource count for trends
 */
export interface DailyResourceCount {
  date: string;
  count: number;
}

/**
 * Get resource change trends over time using VirtualResource API
 */
export async function getResourceTrends(days: number = 6): Promise<DailyResourceCount[]> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/trends`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ days }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch resource trends: ${response.statusText}`);
    }

    const data = await response.json();
    return data.trends || [];
  } catch (error) {
    console.error('Error fetching resource trends:', error);
    throw error;
  }
}

/**
 * Group resource counts by type for chart display
 * Returns top 5 resource types by count, with "Others" category for the rest
 */
export function groupResourceCountsByType(counts: ResourceCount[]): Array<{
  name: string;
  value: number;
  fill: string;
}> {
  // Aggregate counts by type
  const typeMap = new Map<string, number>();

  counts.forEach(({ type, count }) => {
    const current = typeMap.get(type) || 0;
    typeMap.set(type, current + count);
  });

  // Convert to array and sort by count descending
  const sortedTypes = Array.from(typeMap.entries())
    .sort((a, b) => b[1] - a[1]);

  // Take top 5 and aggregate the rest as "Others"
  const top5 = sortedTypes.slice(0, 5);
  const others = sortedTypes.slice(5);
  const othersTotal = others.reduce((sum, [, count]) => sum + count, 0);

  // Convert to chart data format
  const colors = [
    '#f59e0b', // Amber/Orange
    '#3b82f6', // Blue
    '#10b981', // Green
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#6b7280', // Gray for "Others"
  ];

  const result = top5.map(([name, value], index) => ({
    name,
    value,
    fill: colors[index],
  }));

  // Add "Others" category if there are more than 5 types
  if (othersTotal > 0) {
    result.push({
      name: 'Others',
      value: othersTotal,
      fill: colors[5],
    });
  }

  return result;
}

/**
 * Resource event interface
 */
export interface ResourceEvent {
  action?: string;
  reason?: string;
  actor?: string;
  message?: string;
  time?: string;
  apiVersion?: string;
  type?: string;
  metadata?: {
    name?: string;
    path?: string;
    creationTime?: string;
    [key: string]: unknown;
  };
  details?: {
    message?: string;
    reason?: string;
    eventType?: string;
    timestamp?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Get resource events request
 */
export interface GetResourceEventsRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
  limit?: number;
  beforeEventTime?: string;
}

/**
 * Get resource events response
 */
export interface GetResourceEventsResponse {
  events: ResourceEvent[];
  count: number;
  remaining_count?: number;
  next_before_event_time?: string;
}

/**
 * Get business events for a specific resource
 */
export async function getResourceEvents(request: GetResourceEventsRequest, signal?: AbortSignal): Promise<GetResourceEventsResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/events`, {
      signal,
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
        limit: request.limit || 100,
        beforeEventTime: request.beforeEventTime,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch resource events: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching resource events:', error);
    throw error;
  }
}

export const MAX_RESOURCE_EVENT_SAMPLES = 10000;
export const WRITE_BLOCK_REASON = 'TooManyModificationRequests';
export const WRITE_BLOCK_WINDOW_MS = 10 * 60 * 1000;

export function latestWriteBlocks(events: ResourceEvent[]): Map<string, number> {
  const blocks = new Map<string, number>();
  for (const event of events) {
    const time = Date.parse(event.time ?? '');
    if (event.action === 'update' && event.reason === WRITE_BLOCK_REASON && event.actor && time > 0
      && time > (blocks.get(event.actor) ?? 0)) blocks.set(event.actor, time);
  }
  return blocks;
}

export async function getResourceEventSamples(
  request: Omit<GetResourceEventsRequest, 'limit' | 'beforeEventTime'>,
  samples: number,
  signal?: AbortSignal,
  coverWriteBlockWindows = false,
): Promise<{ events: ResourceEvent[]; historyExhausted: boolean }> {
  if (!Number.isInteger(samples) || samples < 1 || samples > MAX_RESOURCE_EVENT_SAMPLES) {
    throw new Error(`Samples must be a whole number from 1 to ${MAX_RESOURCE_EVENT_SAMPLES}`);
  }
  const events: ResourceEvent[] = [];
  const seen = new Set<string>();
  let beforeEventTime: string | undefined;
  let windowStart = Infinity;
  let pageCount = 0;
  let historyExhausted = false;
  while (true) {
    if (++pageCount > 100) throw new Error('Pre-block event window exceeded 100 pages');
    const page = await getResourceEvents({ ...request, limit: 1000, beforeEventTime }, signal);
    signal?.throwIfAborted();
    const samplePage = page.events.slice(0, Math.max(0, samples - events.length));
    if (coverWriteBlockWindows && events.length < samples) {
      for (const time of latestWriteBlocks([...events, ...samplePage]).values()) {
        windowStart = Math.min(windowStart, time - WRITE_BLOCK_WINDOW_MS);
      }
    }
    const included = windowStart === Infinity ? samplePage : page.events;
    events.push(...included);
    const next = page.next_before_event_time;
    historyExhausted = !next && (page.remaining_count ?? 0) === 0 && included.length === page.events.length;
    const oldestTime = page.events.reduce((oldest, event) => {
      const time = Date.parse(event.time ?? '');
      return time > 0 ? Math.min(oldest, time) : oldest;
    }, Infinity);
    if (events.length >= samples && (windowStart === Infinity || oldestTime <= windowStart)) break;
    if (!next) {
      if ((page.remaining_count ?? 0) > 0) throw new Error('Event pagination stopped without a timestamp boundary');
      break;
    }
    if (!page.events.length || seen.has(next)) throw new Error('Event pagination did not advance');
    seen.add(next);
    beforeEventTime = next;
  }
  return { events, historyExhausted };
}

/**
 * Resource reference interface
 */
export interface ResourceReference {
  [key: string]: unknown;
}

/**
 * Get resource references request
 */
export interface GetResourceReferencesRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
}

/**
 * Get resource references response
 */
export interface GetResourceReferencesResponse {
  references: ResourceReference[];
  count: number;
}

/**
 * Get references for a specific resource
 */
export async function getResourceReferences(request: GetResourceReferencesRequest): Promise<GetResourceReferencesResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/references`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch resource references: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching resource references:', error);
    throw error;
  }
}

/**
 * Get resource request
 */
export interface GetResourceRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
}

/**
 * Get resource response
 */
export interface GetResourceResponse {
  resource: unknown;
}

/**
 * Get a single resource by GVT/name/path
 */
export async function getResource(request: GetResourceRequest): Promise<GetResourceResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/get`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
      }),
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - backend lost the user token, redirect to login
      if (response.status === 401) {
        console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
        window.location.href = '/login';
        throw new Error('Authentication required - redirecting to login');
      }
      throw new Error(`Failed to fetch resource: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching resource:', error);
    throw error;
  }
}

/**
 * Touch resource request
 */
export interface TouchResourceRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
}

/**
 * Touch resource response
 */
export interface TouchResourceResponse {
  resource: unknown;
  message: string;
}

/**
 * Touch a resource to update its modification timestamp
 */
export async function touchResource(request: TouchResourceRequest): Promise<TouchResourceResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/touch`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
      }),
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - backend lost the user token, redirect to login
      if (response.status === 401) {
        console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
        window.location.href = '/login';
        throw new Error('Authentication required - redirecting to login');
      }
      throw new Error(`Failed to touch resource: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error touching resource:', error);
    throw error;
  }
}

/**
 * Update resource request
 */
export interface UpdateResourceRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
  resource: unknown; // The complete resource object to update
}

/**
 * Update resource response
 */
export interface UpdateResourceResponse {
  resource: unknown;
  message: string;
}

export type AccessConfigFieldKind =
  | 'string_list'
  | 'client_limits'
  | 'optional_client_limits'
  | 'integer'
  | 'text';

export interface AccessConfigClientLimit {
  client: string;
  limit: number | null;
}

export interface AccessConfigField {
  key: string;
  label: string;
  description: string;
  section: string;
  kind: AccessConfigFieldKind | string;
  value: unknown;
  rawValue: string;
  emptyHint?: string;
  warnings?: string[];
}

export interface AuthenticatedAccessConfig {
  name: string;
  path: string;
  revision: string;
  fields: AccessConfigField[];
  rawConfig: Record<string, unknown>;
}

/** Read the typed editing model for urm-authenticated-access-config. */
export async function getAuthenticatedAccessConfig(): Promise<AuthenticatedAccessConfig> {
  const response = await fetch(`${API_BASE_URL}/api/authenticated-access-config`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const detail = (await response.text()).trim();
    throw new Error(detail || `Failed to load authenticated access configuration (${response.status})`);
  }

  return response.json();
}

/** Patch only changed access-config values, guarded by the loaded revision. */
export async function patchAuthenticatedAccessConfig(
  revision: string,
  values: Record<string, unknown>,
): Promise<AuthenticatedAccessConfig> {
  const headers = await addCSRFHeader({ 'Content-Type': 'application/json' });
  const response = await fetch(`${API_BASE_URL}/api/authenticated-access-config`, {
    method: 'PATCH',
    credentials: 'include',
    headers,
    body: JSON.stringify({ revision, values }),
  });

  if (!response.ok) {
    const detail = (await response.text()).trim();
    throw new Error(detail || `Failed to update authenticated access configuration (${response.status})`);
  }

  return response.json();
}

/**
 * Update a resource with new content
 */
export async function updateResource(request: UpdateResourceRequest): Promise<UpdateResourceResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/update`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
        resource: request.resource,
      }),
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - backend lost the user token, redirect to login
      if (response.status === 401) {
        console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
        window.location.href = '/login';
        throw new Error('Authentication required - redirecting to login');
      }
      throw new Error(`Failed to update resource: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating resource:', error);
    throw error;
  }
}

/**
 * Request type for fetching resource history
 */
export interface GetResourceHistoryRequest {
  group: string;
  version: string;
  type: string;
  name: string;
  path: string;
  limit?: number; // Max number of generations to fetch (default: 10, max: 100)
}

/**
 * Response type for resource history
 */
export interface GetResourceHistoryResponse {
  history: unknown[];
  count: number;
}

/**
 * Get resource history (previous generations)
 */
export async function getResourceHistory(request: GetResourceHistoryRequest): Promise<GetResourceHistoryResponse> {
  try {
    const headers = await addCSRFHeader({
      'Content-Type': 'application/json',
    });

    const response = await fetch(`${API_BASE_URL}/api/history`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        group: request.group,
        version: request.version,
        type: request.type,
        name: request.name,
        path: request.path,
        limit: request.limit || 10,
      }),
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - backend lost the user token, redirect to login
      if (response.status === 401) {
        console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
        window.location.href = '/login';
        throw new Error('Authentication required - redirecting to login');
      }
      throw new Error(`Failed to get resource history: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching resource history:', error);
    throw error;
  }
}

/**
 * Guru AI Chat Types
 */
export interface GuruChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface GuruToolUse {
  tool_name: string;
  tool_input: unknown;
}

export interface GuruToolResult {
  tool_name: string;
  content: string;
}

// Usage stats for current request
export interface GuruRequestUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

// Complete usage stats including session total
export interface GuruUsage {
  current_request: GuruRequestUsage;
  session_total: GuruRequestUsage;
}

export interface GuruStreamEvent {
  event: 'agent_response' | 'tool_use' | 'tool_result' | 'usage' | 'complete' | 'error';
  data?: unknown;
  // agent_response fields
  content?: string;  // Response content (agent_response)
  end_turn?: boolean;  // Whether the agent has finished its turn (agent_response)
  structured_output?: unknown;  // Structured data extracted per schema (agent_response, only when end_turn=true)
  reasoning?: string;  // Agent reasoning (agent_response)
  response?: string;  // Legacy field, use 'content' instead
  // tool_use fields (flat, not nested)
  tool_name?: string;  // Tool name (tool_use, tool_result)
  status?: string;  // Tool status: 'executing' (tool_use)
  tool_input?: unknown;  // Tool input (tool_use)
  // tool_result fields are also flat (tool_name, content)
  // usage fields
  usage?: GuruUsage;
  // error fields
  error?: string;
  // deprecated nested fields (for backward compatibility)
  tool_use?: GuruToolUse;
  tool_result?: GuruToolResult;
}

export interface GuruChatRequest {
  message: string;
  session_id?: string;
  resource_context?: {
    group: string;
    version: string;
    type: string;
    name: string;
    path: string;
  };
}

/**
 * Get user's OIDC token from backend for Guru authentication
 */
export async function getUserToken(): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user-token`, {
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
        // Give a brief moment for any error handling, then redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
        throw new Error('Session expired - Please log in again');
      }
      throw new Error(`Failed to get user token: ${response.statusText}`);
    }

    const data = await response.json();
    return data.token;
  } catch (error) {
    console.error('Error getting user token:', error);
    throw error;
  }
}

/**
 * Stream chat with Guru AI Agent using SSE
 * Returns an async generator that yields SSE events
 *
 * NOTE: This goes through the backend proxy to avoid CORS issues.
 * The backend forwards the request to the Guru AI Agent with the user's OIDC token.
 */
export async function* streamGuruChat(
  request: GuruChatRequest,
  onEvent?: (event: GuruStreamEvent) => void
): AsyncGenerator<GuruStreamEvent, void, undefined> {
  const headers = await addCSRFHeader({
    'Content-Type': 'application/json',
  });

  const response = await fetch(`${API_BASE_URL}/api/guru/chat/stream`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include session cookie for authentication
    body: JSON.stringify({
      message: request.message,
      session_id: request.session_id,
      resource_context: request.resource_context,
    }),
  });

  if (!response.ok) {
    // Handle 401 Unauthorized - backend lost the user token, redirect to login
    if (response.status === 401) {
      console.error('Authentication token lost (likely due to backend restart). Redirecting to login...');
      // Give a brief moment for any error handling, then redirect
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      throw new Error('Session expired - Please log in again');
    }
    throw new Error(`Failed to stream chat: ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  console.log('[Guru SSE] Starting SSE stream consumption');

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        console.log('[Guru SSE] Stream ended (done=true)');
        break;
      }

      // Decode the chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      console.log('[Guru SSE] Received chunk:', chunk.substring(0, 100) + (chunk.length > 100 ? '...' : ''));

      // Process all complete SSE messages in the buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer

      // Track the event type from 'event:' lines
      let currentEventType: string | null = null;

      for (const line of lines) {
        if (line.startsWith('event:')) {
          // Extract event type from 'event:' line
          currentEventType = line.substring(6).trim();
          console.log('[Guru SSE] SSE event type:', currentEventType);
        } else if (line.startsWith('data:')) {
          try {
            const data = line.substring(5).trim();
            console.log('[Guru SSE] Raw data line (first 200 chars):', data.substring(0, 200) + (data.length > 200 ? '...' : ''));
            console.log('[Guru SSE] Full raw data length:', data.length);

            if (data === '[DONE]') {
              console.log('[Guru SSE] Received [DONE] signal');
              const completeEvent: GuruStreamEvent = { event: 'complete' };
              if (onEvent) onEvent(completeEvent);
              yield completeEvent;
              currentEventType = null; // Reset for next event
              continue;
            }

            console.log('[Guru SSE] Attempting to parse as JSON...');
            // Handle double-encoded JSON (same logic as HTML playground)
            let parsedData;
            try {
              const firstParse = JSON.parse(data);
              if (typeof firstParse === 'string') {
                parsedData = JSON.parse(firstParse);
              } else {
                parsedData = firstParse;
              }
            } catch {
              parsedData = data;
            }

            // Merge the event type from the 'event:' line with the parsed data
            const event: GuruStreamEvent = {
              ...parsedData,
              event: currentEventType || parsedData.event || 'unknown'
            };

            console.log('[Guru SSE] Created event with type:', event.event);
            if (onEvent) onEvent(event);
            yield event;
            currentEventType = null; // Reset for next event
          } catch (error) {
            console.error('[Guru SSE] Error parsing SSE message:', error, 'Line:', line);
          }
        } else if (line.startsWith('id:') || line.startsWith('retry:')) {
          console.log('[Guru SSE] SSE metadata:', line);
        } else if (line.trim()) {
          console.log('[Guru SSE] Unexpected line format:', line);
        }
      }
    }
  } finally {
    console.log('[Guru SSE] Releasing stream reader');
    reader.releaseLock();
  }
}

/**
 * Send a chat message to Guru AI (non-streaming)
 */
export async function sendGuruChat(request: GuruChatRequest): Promise<{
  response: string;
  session_id: string;
  usage?: GuruUsage;
}> {
  const userToken = await getUserToken();

  const guruUrl = 'https://urm-ai-agent.ingress.perf.perf.shoot.canary.k8s-hana.ondemand.com/chat';

  const response = await fetch(guruUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userToken}`,
    },
    body: JSON.stringify({
      message: request.message,
      session_id: request.session_id,
      resource_context: request.resource_context,
    }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      console.error('Authentication failed with Guru AI. Redirecting to login...');
      window.location.href = '/login';
      throw new Error('Authentication required - redirecting to login');
    }
    throw new Error(`Failed to send chat message: ${response.statusText}`);
  }

  return await response.json();
}

// ============================================================================
// Rate-Limit Metrics
// ============================================================================

export interface RatelimitMetricsEntry {
  id: string;
  ts_ms: number;
  pod: string;
  limit_calculated: number;
  limit_to_publish: number;
  limit_with_grace: number;
  inflight: number;
  server_latency: number;
  server_cpu: number;
  server_memory: number;
  server_total_requests: number;
  redis_cpu: number;
  redis_memory: number;
  opensearch_cpu: number;
  opensearch_memory: number;
  opensearch_queues: number;
}

export interface MetricsStorageUsage {
  redis_memory_bytes: number;
  redis_key_count: number;
  measured_at_ms: number;
  approximate: boolean;
}

export interface AccessGuardMetricsEntry {
  action: string;
  limiter_key: string;
  count: number;
}

export interface AccessGuardMetricsResponse {
  entries: AccessGuardMetricsEntry[];
  truncated: boolean;
  next_cursor: string;
  storage?: MetricsStorageUsage;
}

export interface AccessGuardMetricsQuery {
  limit?: number;
  startAfter?: string;
  includeStorage?: boolean;
}

/** Fetches one page of cumulative AccessGuard limit-reached counts. */
export async function getAccessGuardMetrics(
  query: AccessGuardMetricsQuery = {},
  signal?: AbortSignal,
): Promise<AccessGuardMetricsResponse> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.startAfter) params.set('start_after', query.startAfter);
  if (query.includeStorage) params.set('include_storage', 'true');

  const url = `${API_BASE_URL}/api/accessguard-metrics${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { credentials: 'include', signal, cache: 'no-store' });
  if (response.status === 401) throw new Error('Unauthorized');
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch AccessGuard metrics: ${response.status} ${text}`);
  }
  return response.json();
}

export interface RatelimitMetricsResponse {
  entries: RatelimitMetricsEntry[];
  truncated: boolean;
  next_cursor: string;
  storage?: MetricsStorageUsage;
}

export interface RatelimitMetricsQuery {
  from?: number;          // unix ms; optional, defaults to last 3d server-side
  to?: number;            // unix ms; optional, defaults to now server-side
  limit?: number;         // server caps at 250000
  startAfter?: string;    // mutually exclusive with `from`; pass full stream id
  includeStorage?: boolean;
}

/**
 * Fetches a page of rate-limit metrics from the technical-pod read endpoint
 * (proxied via /api/ratelimit-metrics). Use the returned `next_cursor` as
 * `startAfter` on the next call to paginate or tail.
 */
export async function getRatelimitMetrics(
  query: RatelimitMetricsQuery = {},
  signal?: AbortSignal,
): Promise<RatelimitMetricsResponse> {
  const params = new URLSearchParams();
  if (query.from !== undefined) params.set('from', String(query.from));
  if (query.to !== undefined) params.set('to', String(query.to));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.startAfter) params.set('start_after', query.startAfter);
  if (query.includeStorage) params.set('include_storage', 'true');

  const url = `${API_BASE_URL}/api/ratelimit-metrics${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { credentials: 'include', signal, cache: 'no-store' });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch ratelimit metrics: ${response.status} ${text}`);
  }
  return response.json();
}

// ============================================================================
// Workload Metrics
// ============================================================================

export interface WorkloadMetricsEntry {
  username: string;
  resource_type: string;
  action: string;
  api_label: string;
  request_count: number;
  total_processing_time_ns: number;
  request_latency_distribution?: RequestLatencyDistribution;
}

export interface RequestLatencyDistribution {
  lt_25_ms: number;
  '25_to_50_ms': number;
  '50_to_100_ms': number;
  '100_to_250_ms': number;
  '250_to_500_ms': number;
  '500_to_1000_ms': number;
  '1000_to_2500_ms': number;
  '2500_to_5000_ms': number;
  gte_5000_ms: number;
}

export interface WorkloadMetricsInterval {
  start_ms: number;
  end_ms: number;
  dropped_records: number;
  entries: WorkloadMetricsEntry[];
}

export interface WorkloadMetricsResponse {
  intervals: WorkloadMetricsInterval[];
  truncated: boolean;
  next_cursor: string;
  storage?: MetricsStorageUsage;
}

export interface WorkloadMetricsQuery {
  from?: number;
  to?: number;
  limit?: number;
  startAfter?: string;
  minLoad?: number;
  includeStorage?: boolean;
}

/** Fetches one page from the workload-metrics proxy. Pagination remains
 * explicit so callers can stop when their requested time window is complete. */
export async function getWorkloadMetrics(
  query: WorkloadMetricsQuery = {},
  signal?: AbortSignal,
): Promise<WorkloadMetricsResponse> {
  const params = new URLSearchParams();
  if (query.from !== undefined) params.set('from', String(query.from));
  if (query.to !== undefined) params.set('to', String(query.to));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.startAfter) params.set('start_after', query.startAfter);
  if (query.minLoad !== undefined) params.set('min_load', String(query.minLoad));
  if (query.includeStorage) params.set('include_storage', 'true');

  const url = `${API_BASE_URL}/api/workload-metrics${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { credentials: 'include', signal, cache: 'no-store' });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch workload metrics: ${response.status} ${text}`);
  }
  return response.json();
}

// ============================================================================
// API interference analysis
// ============================================================================

export interface InterferenceWorkloadKey {
  resource_type: string;
  action: string;
  api_label: string;
}

export interface InterferenceAnalysisConfig {
  minimum_p95_requests: number;
  allow_mean_latency_fallback?: boolean;
  degradation_z: number;
  minimum_latency_rise: number;
  minimum_latency_rise_ms: number;
  controls_per_event: number;
  reference_workload?: string;
}

export interface InterferenceAnalysisRequest {
  from_ms?: number;
  to_ms?: number;
  workload_intervals?: WorkloadMetricsInterval[];
  ratelimit_entries?: RatelimitMetricsEntry[];
  candidate_weights?: Record<string, number>;
  config: InterferenceAnalysisConfig;
}

export interface DoseResponsePoint {
  rps: number;
  relative_odds: number;
}

export interface InterferenceWorkloadCost {
  workload: InterferenceWorkloadKey;
  fallback_level: number;
  weight: number;
  confidence_low?: number | null;
  confidence_high?: number | null;
  incidents: number;
  confidence: string;
  strongest_lag_seconds: number;
  rates_at_risk: Record<string, number | null | undefined>;
  relative_weights: Record<string, number | null | undefined>;
  fixed_weight_suitable?: boolean | null;
  dose_response: DoseResponsePoint[];
}

export interface EventCandidateLoad {
  workload: InterferenceWorkloadKey;
  lag_seconds: number;
  rps: number;
  change_from_baseline_rps: number;
}

export interface ContributingUser {
  user: string;
  weighted_usage: number;
}

export interface EventTimelinePoint {
  timestamp_ms: number;
  victim_latency_ms?: number | null;
  expected_latency_ms?: number | null;
  candidate_traffic: Array<{ workload: InterferenceWorkloadKey; rps: number }>;
  current_limit?: number | null;
}

export interface DegradationEvent {
  id: string;
  incident_id: string;
  event_start_ms: number;
  event_end_ms: number;
  victim_user: string;
  victim_workload: InterferenceWorkloadKey;
  expected_latency_ms: number;
  actual_latency_ms: number;
  mean_latency_ms: number;
  latency_statistic?: 'p95' | 'mean';
  severity_z: number;
  victim_request_rate: number;
  candidate_loads: EventCandidateLoad[];
  top_contributing_users: ContributingUser[];
  current_limit?: number | null;
  timeline: EventTimelinePoint[];
}

export interface RateLimitReplay {
  available: boolean;
  normalized_budget_multiplier?: number;
  events_with_pre_event_throttling: number;
  events_missed: number;
  event_coverage: number;
  healthy_periods_throttled: number;
  normal_traffic_affected: number;
  median_lead_time_seconds: number;
}

export interface ChronologicalValidation {
  validated: boolean;
  training_end_ms?: number;
  training_incidents: number;
  validation_incidents: number;
  auc?: number | null;
  reason?: string;
}

export interface InterferenceAnalysisResponse {
  interval_seconds: number;
  latency_statistic?: 'p95' | 'mean';
  workload_interval_count: number;
  ratelimit_entry_count: number;
  workload_keys: InterferenceWorkloadKey[];
  workload_costs: InterferenceWorkloadCost[];
  events: DegradationEvent[];
  rate_limit_replay: RateLimitReplay;
  validation: ChronologicalValidation;
  warnings: string[];
}

export async function analyzeInterference(
  request: InterferenceAnalysisRequest,
  signal?: AbortSignal,
): Promise<InterferenceAnalysisResponse> {
  const headers = await addCSRFHeader({ 'Content-Type': 'application/json' });
  if (!new Headers(headers).has('X-CSRF-Token')) {
    throw new Error('Unable to obtain a CSRF token for interference analysis');
  }

  const response = await fetch(`${API_BASE_URL}/api/interference-analysis`, {
    method: 'POST',
    headers,
    credentials: 'include',
    cache: 'no-store',
    signal,
    // Normal UI requests contain only a time range. Keep legacy explicit-data
    // requests compact for offline and compatibility callers.
    body: JSON.stringify({
      ...request,
      ...(request.ratelimit_entries && { ratelimit_entries: request.ratelimit_entries.map(({
        ts_ms,
        pod,
        limit_calculated,
        limit_to_publish,
        limit_with_grace,
      }) => ({ ts_ms, pod, limit_calculated, limit_to_publish, limit_with_grace })) }),
    }),
  });
  if (response.status === 401) throw new Error('Authentication required to run interference analysis');
  if (!response.ok) {
    const message = (await response.text()).trim();
    throw new Error(`Interference analysis failed (${response.status})${message ? `: ${message}` : ''}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Technical API: Deleted Resource
// ---------------------------------------------------------------------------

export interface DeletedResourceQuery {
  group: string;
  version: string;
  type: string;
  name: string;
  path?: string; // defaults to "/" server-side
  uid?: string;  // when set, returns a single historical snapshot
}

/**
 * Retrieves the last-known state of a DELETED resource via the technical-pod
 * endpoint (proxied via /api/technical/deleted-resource). Deleted resources are
 * not discoverable through normal CRUD.
 *
 * With `uid`, the response is a single DeletedResource snapshot. Without it, the
 * response is a DeletedResourceList of every historical identity that lived at
 * that group/version/type + path + name. The caller discriminates on the shape.
 *
 * The return type is intentionally `unknown` (single vs list); the page narrows
 * it at render time.
 */
export async function getDeletedResource(query: DeletedResourceQuery): Promise<unknown> {
  const params = new URLSearchParams();
  params.set('group', query.group);
  params.set('version', query.version);
  params.set('type', query.type);
  params.set('name', query.name);
  if (query.path) params.set('path', query.path);
  if (query.uid) params.set('uid', query.uid);

  const url = `${API_BASE_URL}/api/technical/deleted-resource?${params}`;
  const response = await fetch(url, { credentials: 'include' });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch deleted resource: ${response.status} ${text}`);
  }
  return response.json();
}
